/*
 * @package de.bistum-passau.www
 * @author Martin Hellwagner [martin@fredmansky.at]
 * navMainSubSite
 */

import _debounce from 'lodash/debounce';

const navMainSubSite = {
  cfg: {
    name: 'navMainSubSite',
    selectors: {
      body: 'body',
      navMain: '.m-navMainSubSite',
      linksMain: '.m-navMainSubSite__listItem--level-1',
      linksMainBtns: '.m-navMainSubSite__listItem--level-1 > button, .m-navMainSubSite__listItem--level-1 > a',
      linksSub: '.m-navMainSubSite__listItem--level-2',
      linksSubBtns: '.m-navMainSubSite__listItem--level-2 > button, .m-navMainSubSite__listItem--level-2 > a',
      linksSubMobile: '.m-navMainSubSite__list--level-2--mobile',
      menuMain: '.m-navMainSubSite__list',
      submenu: '.m-navMainSubSite__submenu--level-2',
      subsubmenu: '.m-navMainSubSite__submenu--level-3',
      burgerIcon: '.m-navMainSubSite__burgerIcon',
      closeIcon: '.m-navMainSubSite__closeIcon',
      lastMetaWrapper: '.m-navMainSubSite__headList--mobile-last',
    },

    classes: {
      active: 'is-active',
      visible: 'is-visible',
    },

    keys: {
      enter: 'Enter',
      space: ' ',
      escape: 'Escape',
      tab: 'Tab',
    },

    el: {
      $body: undefined,
      $navMain: undefined,
      $menuMain: undefined,
      $burgerIcon: undefined,
      $closeIcon: undefined,
      $lastMetaWrapper: undefined,
    },

    els: {
      $linkListMain: undefined,
      $linkListMainBtns: undefined,
      $linkListSub: undefined,
      $linkListSubBtns: undefined,
    },

    breakpoint: 1024,
    hoverDelay: 125,
    resizeDebounce: 250,
  },

  isDesktop() {
    return document.documentElement.clientWidth >= this.cfg.breakpoint;
  },

  isTouchDevice() {
    return 'ontouchstart' in window;
  },

  isKey(key, targetKey) {
    return key === targetKey;
  },

  isEnterOrSpace(key) {
    return this.isKey(key, this.cfg.keys.enter) || this.isKey(key, this.cfg.keys.space);
  },

  toggleClass(element, className, action = 'toggle') {
    if (element) {
      element.classList[action](className);
    }
  },

  setElements() {
    const sel = this.cfg.selectors, el = this.cfg.el, els = this.cfg.els;
    el.$body = document.querySelector(sel.body);
    el.$navMain = document.querySelector(sel.navMain);
    el.$menuMain = document.querySelector(sel.menuMain);
    el.$burgerIcon = document.querySelector(sel.burgerIcon);
    el.$closeIcon = document.querySelector(sel.closeIcon);
    el.$lastMetaWrapper = document.querySelector(sel.lastMetaWrapper);
    els.$linkListMain = document.querySelectorAll(sel.linksMain);
    els.$linkListMainBtns = document.querySelectorAll(sel.linksMainBtns);
    els.$linkListSub = document.querySelectorAll(sel.linksSub);
    els.$linkListSubBtns = document.querySelectorAll(sel.linksSubBtns);
  },

  addEventListenersToElement(element, events) {
    if (!element) return;

    events.forEach(({ type, handler, options }) => {
      element.addEventListener(type, handler, options);
    });
  },

  removeEventListenersFromElement(element, events) {
    if (!element) return;

    events.forEach(({ type, handler }) => {
      element.removeEventListener(type, handler);
    });
  },

  getDesktopEvents() {
    return [
      { type: 'mouseenter', handler: this.evtMouseEnter },
      { type: 'mouseleave', handler: this.evtMouseLeave },
    ];
  },

  getMobileEvents() {
    const events = [];

    if (this.isTouchDevice()) {
      events.push(
        { type: 'touchstart', handler: () => { this.cfg.touch = true; } },
        { type: 'touchmove', handler: () => { this.cfg.touch = false; } },
        { type: 'touchend', handler: this.evtFingerTouched }
      );
    } else {
      events.push({ type: 'click', handler: this.evtMouseClick });
    }

    return events;
  },

  addEventListener() {
    const isDesktop = this.isDesktop();
    this.cfg.els.$linkListMain?.forEach(link => {
      this.addEventListenersToElement(link, isDesktop ? this.getDesktopEvents() : this.getMobileEvents());
      if (isDesktop) link.querySelector(':scope > button, :scope > a')?.addEventListener('keydown', this.evtKeyHandler);
    });
    if (isDesktop && this.cfg.els.$linkListSub) {
      this.cfg.els.$linkListSub.forEach(sub => {
        this.addEventListenersToElement(sub, this.getDesktopEvents());
        if (this.isTouchDevice()) sub.addEventListener('touchend', this.evtMouseClick);
        sub.querySelectorAll('a, button, [tabindex]').forEach(el => el.addEventListener('keydown', this.evtKeyHandler));
      });
    }
  },

  removeHoverEventListener() {
    const allEvents = [
      ...this.getDesktopEvents(),
      ...this.getMobileEvents()
    ];

    this.cfg.els.$linkListMain?.forEach((link) => {
      this.removeEventListenersFromElement(link, allEvents);
    });

    this.cfg.els.$linkListSub?.forEach((link) => {
      this.removeEventListenersFromElement(link, allEvents);
    });
  },

  evtMouseEnter(evt) {
    this.handleMenuVisibility(evt.target, 'add');
  },

  evtMouseLeave(evt) {
    setTimeout(() => {
      this.handleMenuVisibility(evt.target, 'remove');
    }, this.cfg.hoverDelay);
  },

  evtMouseClick(evt) {
    this.handleMenuVisibility(evt.target, 'toggle');
  },

  evtFingerTouched(evt) {
    if (this.cfg.touch) {
      this.handleMenuVisibility(evt.target, 'toggle');
    }
  },

  handleMenuVisibility(target, action) {
    this.toggleClass(target, this.cfg.classes.active, action);

    const isMainLink = target.classList.contains(this.cfg.selectors.linksMain.substring(1));
    const menuToToggle = isMainLink
      ? target.querySelector(this.cfg.selectors.submenu)
      : target.querySelector(this.cfg.selectors.subsubmenu);

    this.toggleClass(menuToToggle, this.cfg.classes.visible, action);
  },

  addEventListenerForMobileMenu() {
    const open = () => {
      this.openMobileMenu();
      this.cfg.els.$linkListMainBtns?.[0]?.focus();
    };

    const close = () => {
      this.closeMobileMenu();
      this.cfg.el.$burgerIcon?.focus();
    };

    this.cfg.el.$burgerIcon?.addEventListener('click', open);
    this.cfg.el.$closeIcon?.addEventListener('click', close);

    [this.cfg.el.$burgerIcon, this.cfg.el.$closeIcon].forEach((element) => {
      if (element) {
        element.addEventListener('keydown', (e) => {
          if (this.isEnterOrSpace(e.key) && !this.isDesktop()) {
            e.preventDefault();
            element === this.cfg.el.$burgerIcon ? open() : close();
          }
        });
      }
    });

    this.cfg.els.$linkListMainBtns?.forEach((btn) => {
      btn.addEventListener('keydown', (e) => {
        if (this.isEnterOrSpace(e.key)) {
          e.preventDefault();
          this.handleMobileSubmenuToggle(btn);
        }
      });
    });

    this.cfg.els.$linkListSubBtns?.forEach((btn) => {
      btn.addEventListener('keydown', (e) => {
        if (this.isEnterOrSpace(e.key)) {
          e.preventDefault();
          this.handleMobileLevel3SubmenuToggle(btn);
        }
      });
    });

    this.setupFocusTrap();

    document.addEventListener('keydown', (e) => {
      if (this.isKey(e.key, this.cfg.keys.escape) &&
        this.cfg.el.$menuMain?.classList.contains(this.cfg.classes.visible)) {
        e.preventDefault();
        this.closeMobileMenu();
        this.cfg.el.$burgerIcon?.focus();
      }
    });
  },

  handleMobileSubmenuToggle(btn) {
    const li = btn.closest(this.cfg.selectors.linksMain);
    const submenu = li?.querySelector(this.cfg.selectors.submenu);

    if (!submenu) return;

    li.classList.toggle(this.cfg.classes.active);
    submenu.classList.toggle(this.cfg.classes.visible);

    if (submenu.classList.contains(this.cfg.classes.visible)) {
      submenu.querySelector('a, button')?.focus();
    }
  },

  handleMobileLevel3SubmenuToggle(btn) {
    const liSub = btn.closest(this.cfg.selectors.linksSub);
    const subsubmenu = liSub?.querySelector(this.cfg.selectors.subsubmenu);

    if (!subsubmenu) return;

    liSub.classList.toggle(this.cfg.classes.active);
    subsubmenu.classList.toggle(this.cfg.classes.visible);

    if (subsubmenu.classList.contains(this.cfg.classes.visible)) {
      subsubmenu.querySelector('a, button')?.focus();
    }
  },

  setupFocusTrap() {
    if (!this.cfg.el.$lastMetaWrapper) return;

    const metaLinks = this.cfg.el.$lastMetaWrapper.querySelectorAll('a');
    const lastMetaLink = metaLinks[metaLinks.length - 1];

    if (lastMetaLink) {
      lastMetaLink.addEventListener('keydown', (e) => {
        if (this.isKey(e.key, this.cfg.keys.tab) && !e.shiftKey &&
          this.cfg.el.$menuMain?.classList.contains(this.cfg.classes.visible)) {
          e.preventDefault();
          this.cfg.el.$closeIcon?.focus();
        }
      });
    }
  },

  openMobileMenu() {
    this.toggleMobileMenuState(true);
    this.cfg.el.$closeIcon?.setAttribute('tabindex', '0');
  },

  closeMobileMenu() {
    this.toggleMobileMenuState(false);
    this.clearSubmenuStates();
    this.cfg.el.$closeIcon?.setAttribute('tabindex', '-1');
  },
  
  handleSubButtonNavigation(evt, btn) {
    if (this.isKey(evt.key, this.cfg.keys.tab) && !evt.shiftKey) {
      evt.preventDefault();

      const currentSubmenu = btn.closest(this.cfg.selectors.submenu);
      const currentSubmenuL2Btns = Array.from(currentSubmenu?.querySelectorAll(this.cfg.selectors.linksSubBtns) || []);
      const currentIndex = currentSubmenuL2Btns.indexOf(btn);

      if (currentIndex === currentSubmenuL2Btns.length - 1) {
        const allL1 = Array.from(this.cfg.els.$linkListMainBtns);
        const currentL1Item = btn.closest(this.cfg.selectors.linksMain);
        const currentL1Index = allL1.findIndex(l1Btn =>
          l1Btn.closest(this.cfg.selectors.linksMain) === currentL1Item
        );

        if (currentL1Index !== -1 && currentL1Index < allL1.length - 1) {
          const nextL1 = allL1[currentL1Index + 1];
          nextL1?.focus();
          return true;
        }
      } else {
        const nextL2 = currentSubmenuL2Btns[currentIndex + 1];
        nextL2?.focus();
        return true;
      }
    }
    return false;
  },

  handleSubButtonEnter(evt, btn) {
    if (!this.isEnterOrSpace(evt.key)) return false;

    evt.preventDefault();
    const liSub = btn.closest(this.cfg.selectors.linksSub);
    const l3 = liSub?.querySelector(this.cfg.selectors.subsubmenu);

    if (!l3) return false;

    const isOpen = l3.classList.contains(this.cfg.classes.visible);

    this.closeOtherSubSubmenus(btn);

    liSub.classList.toggle(this.cfg.classes.active);
    l3.classList.toggle(this.cfg.classes.visible);

    if (!isOpen) {
      l3.querySelector('a, button')?.focus();
    }
    return true;
  },

  closeOtherSubSubmenus(currentBtn) {
    Array.from(this.cfg.els.$linkListSubBtns)
      .filter(otherBtn => otherBtn !== currentBtn)
      .forEach(otherBtn => {
        const otherLi = otherBtn.closest(this.cfg.selectors.linksSub);
        const otherL3 = otherLi?.querySelector(this.cfg.selectors.subsubmenu);
        otherLi?.classList.remove(this.cfg.classes.active);
        otherL3?.classList.remove(this.cfg.classes.visible);
      });
  },

  handleMainButtonTabNavigation(evt, btn) {
    const allL1 = Array.from(this.cfg.els.$linkListMainBtns);
    const idx1 = allL1.indexOf(btn);

    if (this.isKey(evt.key, this.cfg.keys.tab) && !evt.shiftKey && idx1 > -1 && idx1 !== allL1.length - 1) {
      evt.preventDefault();
      const next = allL1[idx1 + 1];
      next?.focus();
      return true;
    }
    return false;
  },

  handleMainButtonShiftTabNavigation(evt, btn) {
    const allL1 = Array.from(this.cfg.els.$linkListMainBtns);
    const idx1 = allL1.indexOf(btn);

    if (this.isKey(evt.key, this.cfg.keys.tab) && evt.shiftKey && idx1 > -1) {
      const inSub = btn.closest(this.cfg.selectors.submenu);
      if (inSub) {
        evt.preventDefault();
        const parentBtn = inSub.closest(this.cfg.selectors.linksMain)?.querySelector('button');
        parentBtn?.focus();
        return true;
      }

      if (idx1 > 0) {
        evt.preventDefault();
        allL1[idx1 - 1].focus();
        return true;
      }
    }
    return false;
  },
  handleMainButtonEnter(evt, btn) {
    if (!this.isEnterOrSpace(evt.key)) return false;
    evt.preventDefault();
    const li = btn.closest(this.cfg.selectors.linksMain), submenu = li?.querySelector(this.cfg.selectors.submenu);
    if (!submenu) return false;
    const isOpen = submenu.classList.contains(this.cfg.classes.visible);
    this.closeOtherSubmenus(li);
    li.classList.toggle(this.cfg.classes.active);
    submenu.classList.toggle(this.cfg.classes.visible);
    if (!isOpen) submenu.querySelector('li[tabindex], a, button')?.focus();
    return true;
  },
  closeOtherSubmenus(curLi) {
    Array.from(this.cfg.els.$linkListMain).filter(li => li !== curLi).forEach(li => {
      const l2 = li?.querySelector(this.cfg.selectors.submenu);
      li?.classList.remove(this.cfg.classes.active); l2?.classList.remove(this.cfg.classes.visible);
    });
  },
  handleEscapeKey(evt) {
    if (!this.isKey(evt.key, this.cfg.keys.escape)) return false;

    const l3 = evt.target.closest(this.cfg.selectors.subsubmenu);
    if (l3 && l3.classList.contains(this.cfg.classes.visible)) {
      evt.preventDefault();
      l3.classList.remove(this.cfg.classes.visible);
      const parentL2 = l3.closest(this.cfg.selectors.linksSub);
      parentL2?.classList.remove(this.cfg.classes.active);
      parentL2?.querySelector('button')?.focus();
      return true;
    }

    const l2 = evt.target.closest(this.cfg.selectors.submenu);
    if (l2 && l2.classList.contains(this.cfg.classes.visible)) {
      evt.preventDefault();
      l2.classList.remove(this.cfg.classes.visible);
      const parentL1 = l2.closest(this.cfg.selectors.linksMain);
      parentL1?.classList.remove(this.cfg.classes.active);
      parentL1?.querySelector('button')?.focus();
      return true;
    }

    return false;
  },

  evtKeyHandler(evt) {
    const btn = evt.currentTarget, key = evt.key;
    if (btn.matches(this.cfg.selectors.linksSubBtns)) {
      if (this.handleSubButtonNavigation(evt, btn) || this.handleSubButtonEnter(evt, btn)) return;
    }
    if (btn.matches(this.cfg.selectors.linksMainBtns)) {
      if (this.handleMainButtonTabNavigation(evt, btn) || this.handleMainButtonShiftTabNavigation(evt, btn) || this.handleMainButtonEnter(evt, btn)) return;
    }
    this.handleEscapeKey(evt);
  },

  resize() {
    this.closeMobileMenu();
    this.removeHoverEventListener();
    this.addEventListener();
  },

  toggleElementVisibility(element, show) {
    this.toggleClass(element, this.cfg.classes.visible, show ? 'add' : 'remove');
  },

  toggleMobileMenuState(isOpen) {
    const { $burgerIcon, $closeIcon, $menuMain, $body } = this.cfg.el;

    this.toggleElementVisibility($burgerIcon, !isOpen);
    this.toggleElementVisibility($closeIcon, isOpen);
    this.toggleElementVisibility($menuMain, isOpen);
    if ($body) this.toggleClass($body, 'mobileNavOpen', isOpen ? 'add' : 'remove');
  },

  clearSubmenuStates() {
    const submenus = document.querySelectorAll(this.cfg.selectors.submenu);
    submenus.forEach(submenu => {
      submenu.classList.remove(this.cfg.classes.visible);
    });

    const subsubmenus = document.querySelectorAll(this.cfg.selectors.subsubmenu);
    subsubmenus.forEach(subsubmenu => {
      subsubmenu.classList.remove(this.cfg.classes.visible);
    });

    this.cfg.els.$linkListMain?.forEach(link => {
      link.classList.remove(this.cfg.classes.active);
    });

    this.cfg.els.$linkListSub?.forEach(link => {
      link.classList.remove(this.cfg.classes.active);
    });
  },

  init() {
    this.evtMouseEnter = this.evtMouseEnter.bind(this);
    this.evtMouseLeave = this.evtMouseLeave.bind(this);
    this.evtMouseClick = this.evtMouseClick.bind(this);
    this.evtFingerTouched = this.evtFingerTouched.bind(this);
    this.evtKeyHandler = this.evtKeyHandler.bind(this);

    window.addEventListener('load', () => {
      this.setElements();
      this.addEventListener();

      if (!this.isDesktop()) {
        this.addEventListenerForMobileMenu();
      }
    });

    window.addEventListener('resize', _debounce(this.resize.bind(this), this.cfg.resizeDebounce));
  },
};

export default navMainSubSite;
